import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  MenuItem,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Divider,
  useTheme,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  ReferenceLine,
  YAxis,
  CartesianGrid,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import StudentSidebar from "./StudentSidebar";
import axios from "axios";

const StudentDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [studentName, setStudentName] = useState("");
  const [folderSummary, setFolderSummary] = useState(null);
  const [quizHistory, setQuizHistory] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [averageResults, setAverageResults] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dateFilter, setDateFilter] = useState("newest");
  const [statusFilter, setStatusFilter] = useState("all");
  const [folderPage, setFolderPage] = useState(0);
  const [folderRowsPerPage, setFolderRowsPerPage] = useState(5);

  useEffect(() => {
    const token = sessionStorage.getItem("student_token");
    const name = sessionStorage.getItem("student_name");
    const email = sessionStorage.getItem("student_email");
    if (!token) {
      navigate("/login");
    } else {
      setStudentName(name || "Student");
      fetchFolderSummary(token);
      fetchQuizHistory(token);
      fetchAverageResults(token);
    }
  }, [navigate]);

  const fetchFolderSummary = async (token) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/student/folder-summary/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch Course summary");
      const data = await response.json();
      setFolderSummary(data);
    } catch (error) {
      console.error("Error fetching Course summary:", error);
    }
  };

  const filterLast30Days = (results) => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));
    return results
      .filter((quiz) => new Date(quiz.date) >= thirtyDaysAgo)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const fetchQuizHistory = async (token) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await axios.get(`${API_BASE_URL}/quiz-history/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const history = res.data.history || [];
      setQuizHistory(history);
      const results = history.flatMap((quiz) =>
        quiz.attempts.map((attempt) => ({
          name: quiz.title,
          score: parseFloat(attempt.final_score),
          status: attempt.result,
          date: attempt.ended_at,
        }))
      );
      const filteredResults = filterLast30Days(results);
      setQuizResults(filteredResults);
    } catch (err) {
      console.error("Error fetching quiz history:", err);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
        >
          <p style={{ color: theme.palette.text.primary, fontWeight: 600, margin: 0 }}>
            {data.name}
          </p>
          <p style={{ color: theme.palette.text.secondary, margin: "4px 0" }}>
            Score: {data.score}
          </p>
          <p style={{ color: theme.palette.text.secondary, margin: 0, fontSize: "0.875rem" }}>
            Date: {new Date(data.date).toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  const fetchAverageResults = async (token) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await axios.get(`${API_BASE_URL}/quiz/student/average-results/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAverageResults(res.data);
    } catch (err) {
      console.error("Error fetching average results:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter quiz results based on date and status
  const filteredResults = quizResults.filter((quiz) => {
    if (statusFilter !== "all" && quiz.status !== statusFilter) {
      return false;
    }
    if (dateFilter === "7days") {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return new Date(quiz.date) >= sevenDaysAgo;
    } else if (dateFilter === "30days") {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return new Date(quiz.date) >= thirtyDaysAgo;
    }
    return true;
  });

  // Sort by date if needed
  const sortedResults = [...filteredResults].sort((a, b) => {
    if (dateFilter === "newest") return new Date(b.date) - new Date(a.date);
    if (dateFilter === "oldest") return new Date(a.date) - new Date(b.date);
    return 0;
  });

  // Pagination logic
  const paginatedResults = sortedResults.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Pagination logic for folders
  const paginatedFolders = folderSummary?.folders?.slice(
    folderPage * folderRowsPerPage,
    folderPage * folderRowsPerPage + folderRowsPerPage
  ) || [];

  if (loading) {
    return (
      <Box
        sx={{
          textAlign: "center",
          mt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        <Typography
          variant="h6"
          mt={2}
          sx={{ color: theme.palette.text.secondary, fontWeight: "600" }}
        >
          Loading your dashboard...
        </Typography>
      </Box>
    );
  }

  // Use folderSummary.total_assigned_quizzes for total quizzes
  const totalQuizzes = folderSummary?.total_assigned_quizzes || 0;
  // Use averageResults.average_passing_percentage for average score
  const avgScore = averageResults?.average_passing_percentage || 0;
  const passedQuizzes = averageResults?.total_pass || 0;
  const failedQuizzes = averageResults?.total_fail || 0;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 1200,
        mx: "auto",
        minHeight: "100vh",
        background: theme.palette.background.default,
        borderRadius: "24px",
        // boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: "800",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Student Dashboard
        </Typography>
      </Box>

      {/* Profile Card */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: "700", mb: 2 }}>
          Profile
        </Typography>
        <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
              Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
              {studentName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
              Email
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
              {sessionStorage.getItem("student_email") || "N/A"}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Folder Summary Section */}
      {folderSummary && (
        <Paper
          sx={{
            p: 4,
            mb: 4,
            borderRadius: "16px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
            border: `1px solid ${theme.palette.divider}`,
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
            },
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: "700", mb: 2 }}>
            Course Summary
          </Typography>
          <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2, mb: 3 }}>
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: theme.palette.mode === 'light' ? "#f0f8ff" : "rgba(25, 118, 210, 0.2)",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                Total Assigned Courses
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "700", color: "#1976d2" }}>
                {folderSummary.total_assigned_folders}
              </Typography>
            </Paper>
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: theme.palette.mode === 'light' ? "#fff0ff" : "rgba(211, 47, 47, 0.2)",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                Total Assigned Quizzes
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "700", color: "#d32f2f" }}>
                {totalQuizzes}
              </Typography>
            </Paper>
          </Box>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: "600", mb: 2 }}>
            Courses
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: "12px", overflow: "hidden", backgroundColor: theme.palette.background.paper }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)" }}>
                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Course Name</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Quiz Count</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Total Questions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedFolders.map((folder) => (
                  <TableRow key={folder.id} sx={{ "&:hover": { bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.3)" } }}>
                    <TableCell sx={{ color: theme.palette.text.primary }}>{folder.name}</TableCell>
                    <TableCell sx={{ color: theme.palette.text.primary }}>{folder.quiz_count}</TableCell>
                    <TableCell sx={{ color: theme.palette.text.primary }}>{folder.questions.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={folderSummary?.folders?.length || 0}
            rowsPerPage={folderRowsPerPage}
            page={folderPage}
            onPageChange={(event, newPage) => setFolderPage(newPage)}
            onRowsPerPageChange={(event) => {
              setFolderRowsPerPage(parseInt(event.target.value, 10));
              setFolderPage(0);
            }}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          />
        </Paper>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2, mb: 4 }}>
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            bgcolor: theme.palette.mode === 'light' ? "#fffff0" : "rgba(255, 255, 240, 0.2)",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
            Avg. Score
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: "700", color: "#d32f2f" }}>
            {avgScore}%
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            bgcolor: theme.palette.mode === 'light' ? "#f0fff0" : "rgba(46, 125, 50, 0.2)",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
            Passed
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: "700", color: "#2e7d32" }}>
            {passedQuizzes}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            bgcolor: theme.palette.mode === 'light' ? "#fff0f5" : "rgba(198, 40, 40, 0.2)",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
            Failed
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: "700", color: "#c62828" }}>
            {failedQuizzes}
          </Typography>
        </Paper>
      </Box>

      {/* Quiz Results Table and Chart */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: "700", mb: 2 }}>
          Quiz Results
        </Typography>
        <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: theme.palette.text.primary }}>Date</InputLabel>
            <Select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              label="Date"
              sx={{ color: theme.palette.text.primary }}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="7days">Last 7 Days</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: theme.palette.text.primary }}>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
              sx={{ color: theme.palette.text.primary }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="Pass">Pass</MenuItem>
              <MenuItem value="Fail">Fail</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} sx={{ borderRadius: "12px", overflow: "hidden", backgroundColor: theme.palette.background.paper }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)" }}>
                <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Quiz Name</TableCell>
                <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Score</TableCell>
                <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResults.map((quiz, index) => (
                <TableRow key={index} sx={{ "&:hover": { bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.3)" } }}>
                  <TableCell sx={{ color: theme.palette.text.primary }}>{quiz.name}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>{quiz.score}</TableCell>
                  <TableCell>
                    <Chip
                      label={quiz.status}
                      size="small"
                      sx={{
                        bgcolor: quiz.status === "Pass" ? "#4caf50" : "#f44336",
                        color: "white",
                        fontWeight: "bold",
                        borderRadius: "8px",
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedResults.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
          }}
        />
      </Paper>

      {/* Quiz Results Chart */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ background: "transparent", borderRadius: "12px", p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: "700", mb: 0.5 }}>
                Quiz Results Overview
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Showing data of last 30 days
              </Typography>
            </Box>
            <Box sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "8px",
              px: 2,
              py: 1
            }}>
              <Typography variant="body2" sx={{ color: "white", fontWeight: "600" }}>
                {quizResults.length} Quizzes
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />

          <Box sx={{
            overflowX: "auto",
            overflowY: "hidden",
            "&::-webkit-scrollbar": {
              height: "8px",
              mt: 2
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === 'light' ? "#f1f5f9" : "rgba(55, 65, 81, 0.5)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              },
            },
          }}>
            <Box sx={{ minWidth: Math.max(600, quizResults.length * 80) }}>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={quizResults}
                  margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                >
                  <defs>
                    <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#667eea" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#764ba2" stopOpacity={0.9} />
                    </linearGradient>
                    <linearGradient id="colorNegative" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ff6b6b" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#ee5a6f" stopOpacity={0.9} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />

                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12, fontWeight: 500 }}
                    stroke={theme.palette.divider}
                  />

                  <YAxis
                    domain={["dataMin - 2", "dataMax + 2"]}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12, fontWeight: 500 }}
                    stroke={theme.palette.divider}
                    label={{
                      value: "Score",
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: theme.palette.text.secondary, fontWeight: 600 }
                    }}
                  />

                  <Tooltip content={<CustomTooltip />} />

                  <Legend
                    wrapperStyle={{
                      paddingTop: "20px",
                    }}
                    iconType="circle"
                  />

                  <ReferenceLine
                    y={0}
                    stroke={theme.palette.text.secondary}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />

                  <Bar
                    dataKey="score"
                    fill="url(#colorPositive)"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={60}
                  >
                    {quizResults.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.score < 0 ? "url(#colorNegative)" : "url(#colorPositive)"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <Box sx={{
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            gap: 3,
            flexWrap: "wrap"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              }} />
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Positive Score
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)"
              }} />
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Negative Score
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, ml: "auto" }}>
              💡 Scroll horizontally to view all results
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default StudentDashboard;
